import {
  Avatar,
  Box,
  ClickAwayListener,
  Divider,
  FormGroup,
  FormControlLabel,
  Grid,
  Grow,
  InputAdornment,
  MenuItem,
  MenuList,
  Tooltip,
  tooltipClasses,
  Paper,
  Popper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles, styled } from "@mui/styles";
import { useTranslation } from "react-i18next";

import Search from "@mui/icons-material/Search";
import { ReactComponent as Nodata } from "../../../../assets/icons/nodata.svg";
import { ReactComponent as ChevronExpandDown } from "../../../../assets/icons/chevron-expand-down.svg";
import { ReactComponent as ChevronExpandUp } from "../../../../assets/icons/chevron-expand-up.svg";
import { ReactComponent as FilterStatusIcon } from "../../../../assets/icons/filter-status.svg";
import { ReactComponent as CheckGreenCircleIcon } from "../../../../assets/icons/check-green-circle.svg";
import { ReactComponent as InfoIcon } from "../../../../assets/icons/info.svg";

import CustomPagination from "../../../../components/CustomPagination";
import CustomCheckbox from "../../../../components/CustomCheckbox";

import { getEvaluationDashboard } from "../../../../actions/managerDashboard";

import ApexCharts from "react-apexcharts";
import * as Util from "../../../../services/global.service";
import cssVariables from "../../../../theme/cssVariables";
import _ from "lodash";

const BootstrapTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: "#343A40",
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#343A40",
    color: "#FFFFFF",
    padding: "11px 15px",
    maxWidth: "500px!important",
  },
}));


const COLUMNS = {
  score: [
    //{ field: 'employee_joblevel', label: 'jobLevel' },
    { field: 'employee_organization', label: 'organization' },
    { field: 'classification', label: 'classification' },
    { field: 'performance', label: 'goal' },
    { field: 'potential', label: 'competency' },
    { field: 'overall_rating', label: 'overallRating' },
    { field: 'performance_rating', label: 'goalRating' },
    { field: 'potential_rating', label: 'competencyRating' },
  ]
}

const EvaluationManagerDashboard = (props) => {
  const { t, i18n } = useTranslation();
  const managerDashboardState = useSelector((state) => state.ManagerDashboard);
  const dispatch = useDispatch();
  const useStyles = makeStyles({
    flexPaper: {
      flex: 1,
      minWidth: 350,
      boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)!important",
      borderRadius: "10px!important",
    },
    paperRoot: {
      display: "flex",
      width: "100%",
      flexWrap: "wrap",
    },
    toggleBtnRoot: {
      backgroundColor: "#FFF",
    },
    toggleBtnSelected: {
      backgroundColor: `${cssVariables.color.primary}!important`,
      border: "1px solid #DADADA!important",
      color: "#FFF!important",
    },
    checkGreenSvg: {
      background: "#FFF",
      borderRadius: "50%",
    },
    fillSvg: {
      "& > path": {
        fill: "#6B6B6B",
        width: "24px !important",
        height: "24px !important",
      },
      "& g > path": {
        fill: "#6B6B6B",
        width: "24px !important",
        height: "24px !important",
      },
    },
  });
  const classes = useStyles();

  let statusChartOptions = {
    chart: {
      offsetX: 30,
    },
    series: [10, 15, 20],
    options: {
      pie: {
        donut: {
          labels: {
            show: false,
            name: {
              show: true,
              offsetY: 25,
              formatter: () => t("mygoal.label.complete"),
            },
            value: {
              show: true,
              fontSize: "30px",
              fontFamily: "Kanit",
              fontWeight: 600,
              color: undefined,
              offsetY: -15,
              color: "#5DA196",
              formatter: function (val) {
                return val;
              },
            },
            total: {
              show: true,
              showAlways: true,
              fontSize: "14px",
              fontFamily: "Kanit",
              fontWeight: 300,
              color: "#A3A3A3",
              background: "#fff",
            },
          },
        },
      },
      colors: ["#EAEAEA", "#EFB76D", "#5DA196"],
      states: {
        hover: {
          filter: {
            type: "darken",
            value: 1,
          },
        },
      },
      stroke: {
        width: 0,
        colors: ["#fff"],
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        enabledOnSeries: undefined,
        shared: true,
        followCursor: false,
        intersect: false,
        inverseOrder: false,
        custom: undefined,
        fillSeriesColor: false,
        theme: false,
        onDatasetHover: {
          highlightDataSeries: false,
        },
        style: {
          fontSize: "16px",
          fontFamily: "Kanit",
          fontWeight: 300,
        },
        x: {
          show: true,
        },
        y: {
          title: {
            formatter: (seriesName) => seriesName + ":",
          },
          style: {
            fontSize: "16px",
            fontFamily: "Kanit",
            fontWeight: 300,
          },
          formatter: function (val) {
            return val;
          },
        },
        marker: {
          show: true,
        },
        items: {
          display: "flex",
        },
        fixed: {
          enabled: false,
          position: "topRight",
          offsetX: 0,
          offsetY: 0,
        },
      },
      fill: {
        opacity: 1,
        // type: ['solid', 'gradient'],
        // colors: '#F0F0F0'
      },
      legend: {
        show: false,
      },
    },
  };
  const columnWidths = {
    eval: [
      "calc(35% - 12px)",
      "calc(35% - 12px)",
      "calc(12% - 12px)",
      "calc(12% - 12px)",
      "calc(10% - 12px)",
    ],
    score: [
      "calc(20% - 5px)",
      "calc(11% - 5px)",
    ],
  };

  const classificationListColors = [
    {
      code: "star",
      label: "Star",
      color: "#52A934",
    },
    {
      code: "high_potential",
      label: "High Potentials",
      color: "#AECC52",
    },
    {
      code: "high_performance",
      label: "High Performers",
      color: "#AECC52",
    },
    {
      code: "dysfunctional_genius",
      label: "Dysfunctional Geniuses",
      color: "#FFC633",
    },
    {
      code: "core_player",
      label: "Core Players",
      color: "#FFC633",
    },
    {
      code: "workhorses",
      label: "Workhorses",
      color: "#FFC633",
    },
    {
      code: "up_or_out_dilemma",
      label: "Up or Out Dilemmas",
      color: "#F19562",
    },
    {
      code: "up_or_out_grinder",
      label: "Up or Out Grinders",
      color: "#F19562",
    },
    {
      code: "bad_hire",
      label: "Bad Hires",
      color: "#DF5B31",
    },
  ];
  const [filtersQuery, setFiltersQuery] = React.useState({
    query: "",
    reqData: {
      organization_id: [],
      cycle_id: "",
    },
  });
  const [statusChart, setStatusChartData] = React.useState(statusChartOptions);
  const [statusChartRaw, setStatusChartRawData] = React.useState({});
  const [totalStatusForms, setTotalStatusForms] = React.useState(0);
  const [currentCycle, setCurrentCycle] = React.useState({});
  const [perPage, setPerPage] = React.useState(50);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPage, setTotalPage] = React.useState(1);
  const [search, setSearch] = React.useState("");
  const [evalType, setEvalType] = React.useState("all");
  const [evalTypeList, setEvalTypeList] = React.useState([
    {
      id: "all",
      name: i18n.language === "th-TH" ? "ประเภททั้งหมด" : "All Types",
    },
  ]);
  const [nineBoxesData, setNineBoxesData] = React.useState([]);
  const [nineBoxesTooltipData, setNineBoxesTooltipData] = React.useState([]);
  const [tableType, setTableType] = React.useState("eval_status");
  const [list, setList] = React.useState([]);
  const [filteredList, setFilteredList] = React.useState([]);

  const anchorRefStatus = React.useRef(null);
  const [openStatus, setOpenStatus] = React.useState(false);
  const [cycleType, setCycleType] = React.useState("360");
  const [checkboxStatus, setCheckboxStatus] = React.useState([
    "In Progress",
    "Completed",
    "Not Started",
  ]);
  const [checkboxStatusList, setCheckboxStatusList] = React.useState([
    "In Progress",
    "Completed",
    "Not Started",
  ]);

  const [dataShow, setDataShow] = React.useState([])

  const [scoreColumnList, setScoreColumnList] = React.useState(COLUMNS.score)


  const handleStatusKeyDown = (event) => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpenStatus(false);
    } else if (event.key === "Escape") {
      setOpenStatus(false);
    }
  };
  const handleCloseStatus = (event) => {
    if (
      anchorRefStatus.current &&
      anchorRefStatus.current.contains(event.target)
    ) {
      return;
    }
    setOpenStatus(false);
  };
  const handleClickStatus = () => {
    setOpenStatus((prevOpen) => !prevOpen);
  };
  const handleCheckboxStatus = (val) => {
    let tmpCheckbox = [];
    if (val === "all") {
      if (checkboxStatus.length >= 0 && checkboxStatus.length < 3) {
        checkboxStatusList.forEach((status) => {
          tmpCheckbox.push(status);
        });
      }
    } else {
      if (checkboxStatus.includes(val)) {
        tmpCheckbox = [...checkboxStatus].filter((status) => status !== val);
      } else {
        tmpCheckbox = [...checkboxStatus, val];
      }
    }
    setCheckboxStatus(tmpCheckbox);
  };

  const loadData = () => {
    dispatch(getEvaluationDashboard(filtersQuery)).then((rs) => {
      const resp = rs?.data ?? {}
      if (resp?.employees) {
        // first sort
        let sortByStatusOrder = ["In Progress", "Completed", "Not Started"];
        const getStatus = (row) => {
          return sortByStatusOrder.indexOf(row.evaluate_form_status);
        };
        resp.employees.sort((b, a) => {
          return getStatus(b) - getStatus(a) || b.due_date - a.due_date;
        });
        setList(resp.employees);

        dispatch({
          type: "SET_TOTAL_EMPLOYEE_DASHBOARD_MANAGER",
          payload: resp?.total_employee ?? 0,
        });
      }

      if (resp?.status) {
        setStatusChartRawData(resp?.status);
      }

      if (resp?.evaluate_types) {
        let getEvalType = resp.evaluate_types;
        let oldEvalTypeList = [
          {
            id: "all",
            name: i18n.language === "th-TH" ? "ประเภททั้งหมด" : "All Types",
          },
        ];
        let newEvalTypeList = [...oldEvalTypeList, ...getEvalType];
        setEvalTypeList(newEvalTypeList);
      }

      if (resp?.ninebox) {
        setNineBoxesData(resp.ninebox);
      }

      if (resp?.ninebox_tooltip) {
        setNineBoxesTooltipData(resp.ninebox_tooltip);
      }

      if (rs?.custom) {
        let dataShowList = [], scoreColumns = [...COLUMNS.score]
        if (rs?.custom?.data_show?.configuration) {
          let custom = _.find(rs?.custom?.data_show?.configuration, { module: 'dashboard_evaluation' })
          if (custom && custom?.value && custom?.value?.length) {

            for (const field of custom?.value) {
              let this_field = field
              if (field === 'job_level') {
                this_field = 'employee_joblevel'
                dataShowList.push(field)

                scoreColumns.push({ field: 'employee_joblevel', label: 'jobLevel' })
              }
              else {
                scoreColumns.push({ field: this_field, label: field })
              }

            }
          }
        }

        console.log('scoreColumnList', 1, scoreColumns)
        if (rs?.custom?.data_hide?.configuration) {
          let custom = _.find(rs?.custom?.data_hide?.configuration, { module: 'dashboard_evaluation' })
          console.log('Hide', custom.value)
          if (custom && custom?.value && custom?.value?.length) {
            for (const field of custom?.value) {
              let idx = _.findIndex(scoreColumns, { field })
              if (idx !== -1) scoreColumns.splice(idx, 1)
            }
          }
        }

        setDataShow(dataShowList)
        console.log('scoreColumnList', 2, scoreColumns)
        setScoreColumnList(scoreColumns)
      }
    });
  };

  const NineBoxes = (props) => {
    const { data } = props;
    let matrixFormatData = [];

    // loop data
    if (data) {
      data.forEach((box) => {
        let formatData = {
          color: box?.color,
          name: box?.label,
          total: box?.total,
          code: box?.code,
        };
        matrixFormatData.push(formatData);
      });

      const styles = {
        leftRotateLabel: {
          width: "40px",
          height: "100%",
          display: "flex",
          justifyContent: "center",
        },
        leftRotateLabelLineHeight: 8,
        leftRotateOuter: {
          width: "40px",
          height: "100%",
          display: "flex",
          justifyContent: "center",
        },
      };
      const getNineboxesTooltip = (code) => {
        let resultHtml = <></>;
        let competency = "";
        let goal = "";
        let definition = "";
        let findTooltip = nineBoxesTooltipData.find((item) => item.code === code);
        if (findTooltip) {
          definition =
            i18n.language === "th-TH"
              ? findTooltip?.description?.th ?? ""
              : findTooltip?.description?.en ?? "";
          competency = findTooltip?.competency ?? "";
          goal = findTooltip?.goal ?? "";
        }

        resultHtml = (
          <>
            <Grid container>
              <Grid item xs={3}>
                <Typography fontSize={16} fontWeight={600}>
                  {t("dashboardManager.label.competency")}:
                </Typography>
              </Grid>
              <Grid item xs="auto">
                <Typography
                  fontSize={16}
                  fontWeight={400}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {competency}
                </Typography>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={3}>
                <Typography fontSize={16} fontWeight={600}>
                  {t("dashboardManager.label.goal")}:
                </Typography>
              </Grid>
              <Grid item xs="auto">
                <Typography fontSize={16} fontWeight={400}>
                  {" "}
                  {goal}{" "}
                </Typography>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={3}>
                <Typography fontSize={16} fontWeight={600}>
                  {t("dashboardManager.label.definition")}:
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <Typography
                  fontSize={16}
                  fontWeight={400}
                  color="red"
                  sx={{ whiteSpace: "normal", lineBreak: "break-all" }}
                  dangerouslySetInnerHTML={{ __html: definition }}
                >
                </Typography>
              </Grid>
            </Grid>
          </>
        );
        return resultHtml;
      };
      return (
        <>
          <Box
            sx={{
              display: "flex",
              height: "356px",
              width: "auto",
              position: "relative",
              mt: "10px",
              alignItems: "flex-end",
              justifyContent: "center",
              mr: "10px",
            }}
          >
            <div id="pointer-top" />
            <Typography
              component="div"
              sx={{
                position: "absolute",
                transform: "rotate(-90deg)",
                whiteSpace: "nowrap",
                mb: i18n.language === "th-TH" ? "30px" : "45px",
                textTransform: "uppercase",
              }}
              fontSize="14px"
              fontWeight="500"
              lineHeight={1}
            >
              {t("dashboardManager.label.competency")}
            </Typography>
          </Box>
          <Box
            sx={{ width: "calc(100% - 20px)", height: "90%", minHeight: "405px" }}
          >
            {/* 1 row */}
            <Box sx={{ display: "flex", height: "30%" }}>
              <Box sx={styles.leftRotateLabel}>
                <Typography
                  component="div"
                  sx={{ transform: "rotate(-90deg)", whiteSpace: "nowrap" }}
                  fontSize="14px"
                  fontWeight="500"
                  lineHeight={styles.leftRotateLabelLineHeight}
                >
                  {t("dashboardManager.label.high")}
                </Typography>
              </Box>
              {matrixFormatData.map((box, index) => {
                if (index <= 2)
                  return (
                    <Box sx={{ width: "30%", height: "100%" }}>
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          background: box.color,
                          position: "relative",
                          p: 1,
                          display: "flex",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: "auto",
                          }}
                        >
                          <BootstrapTooltip
                            placement="top"
                            arrow
                            title={getNineboxesTooltip(box?.code)}
                          >
                            <InfoIcon className={classes.fillSvg} />
                          </BootstrapTooltip>
                          <Typography
                            fontSize="10px"
                            fontWeight="400"
                            color="#AAAAAA"
                            sx={{ ml: 1 }}
                          >
                            {box.name}
                          </Typography>
                          <Typography
                            fontSize="36px"
                            fontWeight="600"
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                            }}
                          >
                            {box.total}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
              })}
            </Box>
            {/* 1 row */}
            <Box sx={{ display: "flex", height: "30%" }}>
              <Box sx={styles.leftRotateLabel}>
                <Typography
                  component="div"
                  sx={{ transform: "rotate(-90deg)", whiteSpace: "nowrap" }}
                  fontSize="14px"
                  fontWeight="500"
                  lineHeight={styles.leftRotateLabelLineHeight}
                >
                  {t("dashboardManager.label.moderate")}
                </Typography>
              </Box>
              {matrixFormatData.map((box, index) => {
                if (index > 2 && index <= 5)
                  return (
                    <Box sx={{ width: "30%", height: "100%" }}>
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          background: box.color,
                          position: "relative",
                          p: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: "auto",
                          }}
                        >
                          <BootstrapTooltip
                            placement="top"
                            arrow
                            title={getNineboxesTooltip(box?.code)}
                          >
                            <InfoIcon className={classes.fillSvg} />
                          </BootstrapTooltip>
                          <Typography
                            fontSize="10px"
                            fontWeight="400"
                            color="#AAAAAA"
                            sx={{ ml: 1 }}
                          >
                            {box.name}
                          </Typography>
                          <Typography
                            fontSize="36px"
                            fontWeight="600"
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                            }}
                          >
                            {box.total}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
              })}
            </Box>
            {/* 1 row */}
            <Box sx={{ display: "flex", height: "30%" }}>
              <Box sx={styles.leftRotateLabel}>
                <Typography
                  component="div"
                  sx={{ transform: "rotate(-90deg)", whiteSpace: "nowrap" }}
                  fontSize="14px"
                  fontWeight="500"
                  lineHeight={styles.leftRotateLabelLineHeight}
                >
                  {t("dashboardManager.label.low")}
                </Typography>
              </Box>
              {matrixFormatData.map((box, index) => {
                if (index > 5 && index <= 8)
                  return (
                    <Box sx={{ width: "30%", height: "100%" }}>
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          background: box.color,
                          position: "relative",
                          p: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: "auto",
                          }}
                        >
                          <BootstrapTooltip
                            placement="top"
                            arrow
                            title={getNineboxesTooltip(box?.code)}
                          >
                            <InfoIcon className={classes.fillSvg} />
                          </BootstrapTooltip>
                          <Typography
                            fontSize="10px"
                            fontWeight="400"
                            color="#AAAAAA"
                            sx={{ ml: 1 }}
                          >
                            {box.name}
                          </Typography>
                          <Typography
                            fontSize="36px"
                            fontWeight="600"
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                            }}
                          >
                            {box.total}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
              })}
            </Box>
            {/* 1 row */}
            <Box sx={{ display: "flex", height: "10%", alignItems: "center" }}>
              <Box sx={{ width: "40px" }} />
              <Box sx={{ width: "30%" }}>
                <Typography
                  component="div"
                  sx={{ whiteSpace: "nowrap" }}
                  fontSize="14px"
                  fontWeight="500"
                  textAlign="center"
                >
                  {t("dashboardManager.label.low")}
                </Typography>
              </Box>
              <Box sx={{ width: "30%" }}>
                <Typography
                  component="div"
                  sx={{ whiteSpace: "nowrap" }}
                  fontSize="14px"
                  fontWeight="500"
                  textAlign="center"
                >
                  {t("dashboardManager.label.moderate")}
                </Typography>
              </Box>
              <Box sx={{ width: "30%" }}>
                <Typography
                  component="div"
                  sx={{ whiteSpace: "nowrap" }}
                  fontSize="14px"
                  fontWeight="500"
                  textAlign="center"
                >
                  {t("dashboardManager.label.high")}
                </Typography>
              </Box>
            </Box>
            {/* pointer at the bottom */}
            <Box
              sx={{
                display: "flex",
                height: "20px",
                width: "100%",
                position: "relative",
                mt: "5px",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <div
                id="pointer-right"
                style={{ marginLeft: "40px", marginRight: "30px" }}
              />
              <Typography
                component="div"
                sx={{
                  position: "absolute",
                  whiteSpace: "nowrap",
                  ml: "48px",
                  textTransform: "uppercase",
                }}
                fontSize="14px"
                fontWeight="500"
                lineHeight={1}
              >
                {t("dashboardManager.label.goal")}
              </Typography>
            </Box>
          </Box>
        </>
      );

    } else {

      return (<></>)
    }

  };

  const handleChangeTableType = (_, val) => {
    if (val !== null) {
      setTableType(val);
    }
  };

  const handleSearch = (val, filters) => {
    setSearch(val);
    // doSearch(val, filters)
  };
  // const doSearch = useCallback(debounce((word, oldFilters) => {
  //     let filters = {
  //         ...oldFilters,
  //         ...{
  // 			reqData: {
  // 				...oldFilters.reqData,
  // 				"word": word
  // 			}
  // 		}
  //     }
  //     setFiltersQuery(filters)
  // }, 2000), [])

  const handleChangeEvalType = (val) => {
    setEvalType(val);
  };

  const handlePerPageChange = (event) => {
    setPerPage(event.target.value);
  };

  const handleSetCurrentPage = (value) => {
    const getSelectedPage = value.filter(
      (item) => item.type === "page" && item.selected
    );
    setCurrentPage(getSelectedPage.length > 0 ? getSelectedPage[0].page : 1);
  };

  const handleTotalPage = () => {
    const totalPage =
      Math.ceil(filteredList.length / perPage) === 0
        ? 1
        : Math.ceil(filteredList.length / perPage);
    setTotalPage(totalPage);
  };

  const sortData = (key, direction) => {
    let result = [...filteredList];
    if (tableType === "eval_status") {
      result = result.sort((a, b) => {
        if (direction) {
          return String(a[key]).localeCompare(String(b[key]));
        } else {
          return String(b[key]).localeCompare(String(a[key]));
        }
      });
    } else {
      result = result.sort((b, a) => {
        if (direction) {
          if (
            key !== "classification" &&
            key !== "performance" &&
            key !== "potential"
          ) {
            return String(a[key]).localeCompare(String(b[key]));
          } else {
            return String(a[key].label).localeCompare(String(b[key].label));
          }
        } else {
          if (
            key !== "classification" &&
            key !== "performance" &&
            key !== "potential"
          ) {
            return String(b[key]).localeCompare(String(a[key]));
          } else {
            return String(b[key].label).localeCompare(String(a[key].label));
          }
        }
      });
    }
    setFilteredList(result);
  };

  const formatStatusChart = (data) => {
    let name = [];
    let seriesData = [];
    let chartData = data;
    let sortChartData = [];
    let sortOrder = ["draft", "pending", "approved"];
    if (chartData) {
      sortChartData = Object.keys(chartData).map((item) => {
        return {
          key: item,
          label: chartData[item].label,
          total: chartData[item].total,
        };
      });
      sortChartData.sort(
        (a, b) => sortOrder.indexOf(a.key) - sortOrder.indexOf(b.key)
      );
      sortChartData.forEach((item) => {
        name.push(item.label ?? "-");
        seriesData.push(item.total ?? 0);
      });
    }
    let totalForms = seriesData.reduce((prev, cur) => prev + cur, 0);
    setTotalStatusForms(totalForms);
    let tempChartData = {
      ...statusChart,
      ...{
        series: seriesData,
      },
    };
    tempChartData.options = {
      ...tempChartData.options,
      ...{
        labels: name,
      },
    };
    setStatusChartData(tempChartData);
  };

  const StatusChartLegend = (data) => {
    let seriesData = data?.series;
    let percentSeriesData = seriesData.map((series) => {
      return (series / totalStatusForms) * 100;
    });
    let labels = data?.options?.labels;
    let colors = data?.options?.colors;
    const PreDom = (props) => {
      let count = seriesData.length <= 5 ? 12 : 6;
      return (
        <Grid item xs={count} sx={{ maxWidth: "50%" }}>
          {props.children}
        </Grid>
      );
    };
    let resultDom = null;
    let listDom = [];
    if (seriesData && labels && colors) {
      seriesData.forEach((series, index) => {
        listDom.push(
          <Grid container mb="5px" key={`series-${index}`}>
            <Grid item xs="auto" display="flex" alignItems="center">
              <Box
                sx={{
                  borderRadius: "50%",
                  width: "10px",
                  height: "10px",
                  background: colors?.[index] ?? "#EAEAEA",
                  mr: 2,
                }}
              />
              <Typography fontSize="16px" fontWeight="400" color="#AAAAAA">
                {labels?.[index] ?? "-"}
              </Typography>
            </Grid>
            <Grid item ml="auto" display="flex" alignItems="center">
              <Typography fontSize="16px" fontWeight="400" color="#AAAAAA">
                {series} (
                {!isNaN(percentSeriesData?.[index]?.toFixed(2))
                  ? percentSeriesData?.[index]?.toFixed(2)
                  : "0.00"}
                %)
              </Typography>
            </Grid>
          </Grid>
        );
      });
    }
    resultDom = <PreDom>{listDom}</PreDom>;

    return resultDom;
  };

  const statusBox = (data, isCenter = false) => {
    let colors = {
      inProgress: {
        font: "#EFB76D",
        background: "rgba(239, 183, 109, 0.1)",
      },
      completed: {
        font: "#5DA196",
        background: "rgba(93, 161, 150, 0.1)",
      },
      notStarted: {
        font: "#DADADA",
        background: "#FFF",
      },
    };

    const statusKey = (status) => {
      if (status === "In Progress" || status === "กำลังดำเนินการ") {
        return "inProgress";
      } else if (status === "Completed" || status === "สำเร็จ") {
        return "completed";
      } else if (status === "Not Started" || status === "ยังไม่ได้ดำเนินการ") {
        return "notStarted";
      }
    };
    return (
      <Box
        sx={{
          backgroundColor: colors?.[statusKey(data)]?.background ?? "#fff",
          width: "120px",
          paddingTop: "8px",
          paddingBottom: "8px",
          height: "40px",
          textAlign: "center",
          borderRadius: "5px",
          margin: isCenter ? "0 auto" : "none",
          border:
            data === "ยังไม่ได้ดำเนินการ" || data === "Not Started"
              ? "1px solid #DADADA4A"
              : `0px`,
        }}
      >
        <Typography
          fontSize={16}
          lineHeight="21px"
          fontWeight="500"
          color={colors?.[statusKey(data)]?.font ?? "#404349"}
        >
          {Util.upperFirstWord(data ?? "-")}
        </Typography>
      </Box>
    );
  };

  const getClassificationListColor = (val) => {
    let getColor = classificationListColors.filter((list) => list.code === val);
    let result = "#404349";
    if (getColor.length > 0) {
      result = getColor[0].color;
    }
    return result;
  };

  const currentData = useMemo(() => {
    handleTotalPage();
    const firstPageIndex = (currentPage - 1) * perPage;
    const lastPageIndex = firstPageIndex + perPage;
    if (tableType === "eval_status") {
      return filteredList
        .filter((item) => checkboxStatus.includes(item.evaluate_form_status))
        .slice(firstPageIndex, lastPageIndex);
    } else {
      return filteredList.slice(firstPageIndex, lastPageIndex);
    }
  }, [filteredList, currentPage, perPage, checkboxStatus]);

  const getScoreColumnWidth = () => {
    const fullWidth = 80

    let width = (scoreColumnList.length > 0) ? Math.round(fullWidth / scoreColumnList.length) : fullWidth

    return 'calc(' + width + '% - 5px)'
  }

  // useEffect
  useEffect(() => {
    let query = "";
    if (
      managerDashboardState?.filtersChecked &&
      managerDashboardState?.cycleData?.serial &&
      managerDashboardState?.cycleData?.serial !== ""
    ) {
      let reqData = {
        organization_id: [],
        cycle_id: "",
      };
      let orgIds = managerDashboardState?.filtersChecked?.map((filter) => {
        return `${filter}`;
      });
      setCycleType(managerDashboardState.cycleData.type)
      if (orgIds.length > 0) {
        reqData.organization_id = orgIds;
        reqData.cycle_id = managerDashboardState.cycleData.serial;
        setCurrentCycle(managerDashboardState.cycleData);
        if (evalType !== "all") {
          query = `evaluate_type=${evalType}`;
        }
        setFiltersQuery({ query, reqData });
      }
    }
  }, [managerDashboardState.filtersChecked, evalType]);

  useEffect(() => {
    if (
      filtersQuery?.reqData?.cycle_id !== "" &&
      filtersQuery?.reqData?.organization_id?.length > 0
    )
      loadData();
  }, [filtersQuery]);

  useEffect(() => {
    setCycleType(managerDashboardState.cycleData?.type ?? "annual");
  }, [managerDashboardState.cycleData]);

  useEffect(() => {
    let resultList = JSON.parse(JSON.stringify(list));
    if (search !== "") {
      if (tableType === "eval_status") {
        resultList = resultList.filter(
          (item) =>
            item?.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
            item?.employee_code?.toLowerCase().includes(search.toLowerCase()) ||
            item?.evaluator_name?.toLowerCase().includes(search.toLowerCase())
        );
      } else {
        resultList = resultList.filter((item) =>
          item?.employee_name?.toLowerCase().includes(search.toLowerCase())
        );
      }
    }
    setFilteredList(resultList);
  }, [search, tableType]);

  useEffect(() => {
    if (statusChartRaw) {
      formatStatusChart(statusChartRaw);
    }
  }, [statusChartRaw, currentCycle]);

  useEffect(() => {
    setFilteredList(list);
  }, [list]);
  // end useEffect

  return (
    <>
      <Box id="back-to-top-anchor"> </Box>
      <Grid container columnSpacing={4} my={6}>
        <Grid item xs={12} md={cycleType === "annual" ? 6 : 12}>
          <Paper
            classes={{ root: classes.flexPaper }}
            sx={{ minHeight: "530px", p: "20px" }}
          >
            <Grid container>
              <Grid
                item
                xs="auto"
                display="flex"
                alignItems="center"
                textAlign="left"
              >
                <Typography fontSize={24} fontWeight="600">
                  {t("dashboardManager.label.evaluationStatus")}
                </Typography>
              </Grid>
              <Grid
                item
                ml="auto"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {/* <FormControl fullWidth>
                                    <Select
                                        displayEmpty
                                        id="cycle-select-form"
                                        value={evalType}
                                        onChange={(val) => { handleChangeEvalType(val.target.value) }}
                                        input={<OutlinedInput size="small" />}
                                        sx={
                                            {
                                                width: "140px",
                                                height: "35px"
                                            }
                                        }
                                    >
                                        {evalTypeList.map((option, idx) => {
                                            return <MenuItem key={idx} value={option.id}><Typography style={{ fontWeight: 400, fontSize: '16px', color: '#404349' }}>{option.name}</Typography></MenuItem>
                                        })}
                                    </Select>
                                </FormControl> */}
              </Grid>
            </Grid>
            <Grid container mt={2}>
              <Grid item xs={12}>
                <Box sx={{ minHeight: "280px", position: "relative" }}>
                  <Box
                    sx={{
                      minHeight: "300px",
                      position: "relative",
                      ".apexcharts-tooltip-marker": { borderRadius: "2px" },
                      ".apexcharts-tooltip-y-group > span": {
                        fontWeight: "400",
                      },
                      ".apexcharts-tooltip": {
                        background: "#F7F7F7",
                        fontWeight: "400",
                      },
                      ".apexcharts-legend-text": {
                        color: "#A3A3A3 !important",
                      },
                      ".apexcharts-text": {
                        color: "#A3A3A3 !important",
                        fill: "#A3A3A3  !important",
                      },
                      ".apexcharts-tooltip-title": {
                        background: "#5DA196 !important",
                        color: "#FFFFFF",
                        fontWeight: "400",
                      },
                      height: "250px",
                    }}
                  >
                    <ApexCharts
                      options={statusChart.options}
                      series={statusChart.series}
                      type="donut"
                      height="100%"
                    />
                  </Box>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -70%)",
                    }}
                  >
                    <Typography
                      fontSize="48px"
                      fontWeight="600"
                      textAlign="center"
                    >
                      {totalStatusForms}
                    </Typography>
                    <Typography
                      fontSize="16px"
                      fontWeight="400"
                      color="#AAAAAA"
                    >
                      {t("dashboardManager.label.totalForms")}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
            <Grid
              container
              mt={2}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {StatusChartLegend(statusChart)}
            </Grid>
          </Paper>
        </Grid>
        {(cycleType === "annual" && nineBoxesData?.length > 0) ? (
          <Grid item xs={12} md={6}>
            <Paper
              classes={{ root: classes.flexPaper }}
              sx={{ minHeight: "530px", p: "20px" }}
            >
              <Grid container>
                <Grid item display="flex" alignItems="center" textAlign="left">
                  <Typography fontSize={24} fontWeight="600">
                    {t("dashboardManager.label.competencyVsGoal")}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container mt={3}>
                <Grid
                  item
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="flex-start"
                  xs
                >
                  <NineBoxes data={nineBoxesData} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
      {cycleType === "annual" ? (
        <Grid container mb={0}>
          <Grid item xs>
            <ToggleButtonGroup
              color="primary"
              value={tableType}
              exclusive
              onChange={handleChangeTableType}
              aria-label="Evaluation Data Table"
            >
              <ToggleButton
                value="eval_status"
                sx={{
                  width: "177px",
                  height: "45px",
                  backgroundColor: "#FFF",
                  textTransform: "none",
                  borderRadius: "5px 0px 0px 0px",
                }}
                classes={{ selected: classes.toggleBtnSelected }}
              >
                <Typography fontSize="16px" fontWeight="500">
                  {t("dashboardManager.label.evaluationStatus")}
                </Typography>
              </ToggleButton>
              <ToggleButton
                value="score_statis"
                sx={{
                  width: "177px",
                  height: "45px",
                  backgroundColor: "#FFF",
                  textTransform: "none",
                  borderRadius: "0px 5px 0px 0px",
                }}
                classes={{ selected: classes.toggleBtnSelected }}
              >
                <Typography fontSize="16px" fontWeight="500">
                  {t("dashboardManager.label.scoreStatistical")}
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      ) : (
        <></>
      )}
      {/* TABLE */}
      <Grid container spacing={2}>
        <Grid item xs>
          <Box className={classes.paperRoot}>
            <Paper className={classes.flexPaper}>
              <Grid container spacing={2} px="20px" pt="20px">
                <Grid item>
                  <Typography fontSize={24} fontWeight="600">
                    {tableType === "eval_status"
                      ? t("dashboardManager.label.evaluationStatus")
                      : t("dashboardManager.label.scoreStatistical")}
                  </Typography>
                </Grid>
                <Grid item ml="auto">
                  <TextField
                    id="search-table"
                    fullWidth
                    placeholder={
                      tableType === "eval_status"
                        ? t(
                          "dashboardManager.label.searchPlaceholderEvaluation1"
                        )
                        : t(
                          "dashboardManager.label.searchPlaceholderEvaluation2"
                        )
                    }
                    size="small"
                    sx={{
                      background: "#fff",
                      width: "400px",
                      height: "45px",
                    }}
                    value={search}
                    onChange={(e) => handleSearch(e.target.value, filtersQuery)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search style={{ color: "#C4C4C4" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              {/* TABLE HEADER */}
              {tableType === "eval_status" ? (
                <Box px="20px">
                  <Box
                    px="25px"
                    py="19px"
                    sx={{ display: "flex", flexDirection: "row" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.eval[0],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={14}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.employee")}
                      </Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <ChevronExpandUp
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("employee_name", true)}
                        />
                        <ChevronExpandDown
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("employee_name", false)}
                        />
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.eval[2],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={14}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.organization")}
                      </Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      ></Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <ChevronExpandUp
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("employee_organization", true)}
                        />
                        <ChevronExpandDown
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("employee_organization", false)}
                        />
                      </Typography>
                    </Box>

                    {(dataShow.indexOf('job_level') !== -1) &&
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          width: columnWidths.eval[2],
                        }}
                        pr="15px"
                      >
                        <Typography
                          fontSize={14}
                          lineHeight="23.96px"
                          color="#A0A0A0"
                          fontWeight="500"
                          sx={{
                            whiteSpace: "nowrap",
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "max-content",
                            mr: "10px",
                          }}
                        >
                          {t("dashboardManager.label.jobLevel")}
                        </Typography>
                        <Typography
                          component="span"
                          style={{
                            flexDirection: "column",
                            justifyContent: "center",
                            display: "flex",
                          }}
                        ></Typography>
                        <Typography
                          component="span"
                          style={{
                            flexDirection: "column",
                            justifyContent: "center",
                            display: "flex",
                          }}
                        >
                          <ChevronExpandUp
                            style={{ cursor: "pointer" }}
                            onClick={(e) => sortData("employee_joblevel", true)}
                          />
                          <ChevronExpandDown
                            style={{ cursor: "pointer" }}
                            onClick={(e) => sortData("employee_joblevel", false)}
                          />
                        </Typography>
                      </Box>
                    }

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.eval[1],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={14}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.evaluator")}
                      </Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      ></Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <ChevronExpandUp
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("evaluator_name", true)}
                        />
                        <ChevronExpandDown
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("evaluator_name", false)}
                        />
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.eval[2],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={14}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.type")}
                      </Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <ChevronExpandUp
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("evaluate_type", true)}
                        />
                        <ChevronExpandDown
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("evaluate_type", false)}
                        />
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.eval[3],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={14}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.dueDate")}
                      </Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <ChevronExpandUp
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("due_date", true)}
                        />
                        <ChevronExpandDown
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("due_date", false)}
                        />
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.eval[4],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={14}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.status")}
                      </Typography>
                      <Stack direction="row" spacing={2}>
                        <div>
                          <FilterStatusIcon
                            ref={anchorRefStatus}
                            id="status-filter-button"
                            style={{ cursor: "pointer" }}
                            onClick={handleClickStatus}
                          />
                          <Popper
                            open={openStatus}
                            anchorEl={anchorRefStatus.current}
                            role={undefined}
                            placement="bottom-start"
                            transition
                            disablePortal
                            style={{ zIndex: "1" }}
                          >
                            {({ TransitionProps, placement }) => (
                              <Grow
                                {...TransitionProps}
                                style={{
                                  transformOrigin:
                                    placement === "bottom-start"
                                      ? "left top"
                                      : "left bottom",
                                }}
                              >
                                <Paper sx={{ "margin-top": "5px !important" }}>
                                  <ClickAwayListener
                                    onClickAway={handleCloseStatus}
                                  >
                                    <MenuList
                                      autoFocusItem={openStatus}
                                      id="status-menu"
                                      aria-labelledby="composition-button"
                                      onKeyDown={handleStatusKeyDown}
                                    >
                                      <Box px="16px" py="6px">
                                        <FormGroup>
                                          <FormControlLabel
                                            control={
                                              <CustomCheckbox
                                                indeterminate={
                                                  checkboxStatus.length > 0 &&
                                                  checkboxStatus.length < 3
                                                }
                                                onChange={() =>
                                                  handleCheckboxStatus("all")
                                                }
                                                checked={
                                                  checkboxStatus.length === 3
                                                }
                                              />
                                            }
                                            label={t("label.selectAll")}
                                          />
                                          {checkboxStatusList &&
                                            checkboxStatusList.map((item) => {
                                              return (
                                                <FormControlLabel
                                                  control={
                                                    <CustomCheckbox
                                                      onChange={() =>
                                                        handleCheckboxStatus(
                                                          item
                                                        )
                                                      }
                                                      checked={checkboxStatus.includes(
                                                        item
                                                      )}
                                                    />
                                                  }
                                                  label={
                                                    item === "In Progress" ||
                                                      item === "กำลังดำเนินการ"
                                                      ? t(
                                                        "dashboardManager.label.inProgress"
                                                      )
                                                      : item === "Completed" ||
                                                        item === "เสร็จสิ้น"
                                                        ? t(
                                                          "dashboardManager.label.completed"
                                                        )
                                                        : item ===
                                                          "Not Started" ||
                                                          item ===
                                                          "ยังไม่ได้ดำเนินการ"
                                                          ? t(
                                                            "dashboardManager.label.notStarted"
                                                          )
                                                          : "-"
                                                  }
                                                />
                                              );
                                            })}
                                        </FormGroup>
                                      </Box>
                                    </MenuList>
                                  </ClickAwayListener>
                                </Paper>
                              </Grow>
                            )}
                          </Popper>
                        </div>
                      </Stack>
                    </Box>
                  </Box>
                  <Divider width="100%" />
                </Box>
              ) : (
                <Box px="20px">
                  <Box
                    px="25px"
                    py="19px"
                    sx={{ display: "flex", flexDirection: "row" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.score[0],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={14}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.employeeFullName")}
                      </Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <ChevronExpandUp
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("employee_name", true)}
                        />
                        <ChevronExpandDown
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("employee_name", false)}
                        />
                      </Typography>
                    </Box>



                    {scoreColumnList.map((column, i) => (
                      <Box key={i}
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          width: getScoreColumnWidth(),
                        }}
                        pr="15px"
                      >
                        <Typography
                          fontSize={14}
                          lineHeight="23.96px"
                          color="#A0A0A0"
                          fontWeight="500"
                          sx={{
                            whiteSpace: "nowrap",
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "max-content",
                            mr: "10px",
                          }}
                        >
                          {t("dashboardManager.label." + column.label)}
                        </Typography>
                        <Typography
                          component="span"
                          style={{
                            flexDirection: "column",
                            justifyContent: "center",
                            display: "flex",
                          }}
                        ></Typography>
                        <Typography
                          component="span"
                          style={{
                            flexDirection: "column",
                            justifyContent: "center",
                            display: "flex",
                          }}
                        >
                          <ChevronExpandUp
                            style={{ cursor: "pointer" }}
                            onClick={(e) => sortData(column.field, true)}
                          />
                          <ChevronExpandDown
                            style={{ cursor: "pointer" }}
                            onClick={(e) => sortData(column.field, false)}
                          />
                        </Typography>
                      </Box>
                    ))}

                    {/*
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.score[1],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={14}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.goal")}
                      </Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <ChevronExpandUp
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("performance", true)}
                        />
                        <ChevronExpandDown
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("performance", false)}
                        />
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.score[1],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={14}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.competency")}
                      </Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <ChevronExpandUp
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("potential", true)}
                        />
                        <ChevronExpandDown
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("potential", false)}
                        />
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.score[1],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={14}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.overallRating")}
                      </Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <ChevronExpandUp
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("overall_rating", true)}
                        />
                        <ChevronExpandDown
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("overall_rating", false)}
                        />
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.score[1],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={14}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.goalRating")}
                      </Typography>
                      <Typography
                        component="span"
                        style={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <ChevronExpandUp
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("performance_rating", true)}
                        />
                        <ChevronExpandDown
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("performance_rating", false)}
                        />
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: columnWidths.score[1],
                      }}
                      pr="15px"
                    >
                      <Typography
                        fontSize={14}
                        lineHeight="23.96px"
                        color="#A0A0A0"
                        fontWeight="500"
                        sx={{
                          whiteSpace: "nowrap",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "max-content",
                          mr: "10px",
                        }}
                      >
                        {t("dashboardManager.label.competencyRating")}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          flexDirection: "column",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <ChevronExpandUp
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("potential_rating", true)}
                        />
                        <ChevronExpandDown
                          style={{ cursor: "pointer" }}
                          onClick={(e) => sortData("potential_rating", false)}
                        />
                      </Typography>
                    </Box>
*/}

                  </Box>
                  <Divider width="100%" />
                </Box>
              )}
              {/* TABLE DATA */}
              {currentData && currentData.length > 0 ? (
                currentData.map((row, index) => {
                  if (tableType === "eval_status") {
                    return (
                      <Box px="20px" key={`emp-${index}`}>
                        <Box
                          sx={{
                            width: "100%",
                            position: "relative",
                            py: 1,
                          }}
                        >
                          <Box
                            sx={{
                              height: "96px",
                              background: "#FFF",
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                            }}
                            px="25px"
                          >
                            <Box sx={{ width: columnWidths.eval[0] }} pr="15px">
                              <Grid container spacing={2} flexWrap="nowrap">
                                <Grid item display="flex" alignItems="center">
                                  <Box sx={{ position: "relative" }}>
                                    <Avatar
                                      src={row?.employee_photo}
                                      sx={{
                                        border:
                                          row?.employee_submited === "y"
                                            ? "3px solid #5DA196"
                                            : "none",
                                      }}
                                    />
                                    {row?.employee_submited === "y" && (
                                      <CheckGreenCircleIcon
                                        style={{
                                          position: "absolute",
                                          bottom: 0,
                                          right: 0,
                                          zIndex: 2,
                                        }}
                                        className={classes.checkGreenSvg}
                                      />
                                    )}
                                  </Box>
                                </Grid>
                                <Grid item>
                                  <Grid container>
                                    <Grid
                                      item
                                      display="flex"
                                      alignItems="center"
                                    >
                                      <Typography
                                        fontSize="18px"
                                        fontWeight="500"
                                      >
                                        {row?.employee_name ?? "-"}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                  <Grid container>
                                    <Grid
                                      item
                                      display="flex"
                                      alignItems="center"
                                    >
                                      <Typography
                                        fontSize="14px"
                                        fontWeight="400"
                                        color="#AAAAAA"
                                      >
                                        {row?.employee_position ?? "-"}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Box>

                            <Box
                              sx={{
                                width: columnWidths.eval[2],
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                              }}
                              pr="15px"
                            >
                              <Typography fontWeight="400" fontSize="14px">
                                {row?.employee_organization ?? "-"}
                              </Typography>
                            </Box>

                            {(dataShow.indexOf('job_level') !== -1) &&
                              <Box
                                sx={{
                                  width: columnWidths.eval[2],
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  alignItems: "center",
                                }}
                                pr="15px"
                              >
                                <Typography fontWeight="400" fontSize="14px">
                                  {row?.employee_joblevel ?? "-"}
                                </Typography>
                              </Box>
                            }


                            <Box sx={{ width: columnWidths.eval[1] }} pr="15px">
                              <Grid container spacing={2} flexWrap="nowrap">
                                <Grid item display="flex" alignItems="center">
                                  <Box sx={{ position: "relative" }}>
                                    <Avatar
                                      src={row?.evaluator_photo}
                                      sx={{
                                        border:
                                          row?.evaluator_approved === "y"
                                            ? "3px solid #5DA196"
                                            : "none",
                                      }}
                                    />
                                    {row?.evaluator_approved === "y" && (
                                      <CheckGreenCircleIcon
                                        style={{
                                          position: "absolute",
                                          bottom: 0,
                                          right: 0,
                                          zIndex: 2,
                                        }}
                                        className={classes.checkGreenSvg}
                                      />
                                    )}
                                  </Box>
                                </Grid>
                                <Grid item>
                                  <Grid container>
                                    <Grid
                                      item
                                      display="flex"
                                      alignItems="center"
                                    >
                                      <Typography
                                        fontSize="18px"
                                        fontWeight="500"
                                      >
                                        {row?.evaluator_name ?? "-"}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                  <Grid container>
                                    <Grid
                                      item
                                      display="flex"
                                      alignItems="center"
                                    >
                                      <Typography
                                        fontSize="14px"
                                        fontWeight="400"
                                        color="#AAAAAA"
                                      >
                                        {row?.evaluator_position ?? "-"} (
                                        {row?.evaluator_organization ?? "-"})
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Box>

                            <Box
                              sx={{
                                width: columnWidths.eval[2],
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                              }}
                              pr="15px"
                            >
                              <Typography fontWeight="400" fontSize="14px">
                                {row?.evaluate_type ?? "-"}
                              </Typography>
                            </Box>
                            <Box sx={{ width: columnWidths.eval[3] }} pr="15px">
                              <Typography fontWeight="400" fontSize="14px">
                                {Util.dateDayFormat(row?.due_date) ?? "-"}
                              </Typography>
                            </Box>
                            <Box sx={{ width: columnWidths.eval[4] }} pr="15px">
                              <Typography
                                fontWeight="400"
                                fontSize={14}
                                lineHeight="23.92px"
                                sx={{
                                  width: "100%",
                                }}
                              >
                                {statusBox(row?.evaluate_form_status) ?? "-"}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Divider width="100%" />
                      </Box>
                    );
                  } else {
                    if (!row.is_review) {
                      return (
                        <Box px="20px" key={`emp-${index}`}>
                          <Box
                            sx={{
                              width: "100%",
                              position: "relative",
                              py: 1,
                            }}
                          >
                            <Box
                              sx={{
                                height: "96px",
                                background: "#FFF",
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                              }}
                              px="25px"
                            >
                              <Box
                                sx={{ width: columnWidths.score[0] }}
                                pr="15px"
                              >
                                <Grid container flexWrap="nowrap">
                                  <Grid item display="flex" alignItems="center">
                                    <Avatar src={row?.employee_photo} />
                                  </Grid>
                                  <Grid
                                    item
                                    pl={2}
                                    sx={{ width: "calc(100% - 12px)" }}
                                  >
                                    <Grid container>
                                      <Grid
                                        item
                                        display="flex"
                                        alignItems="center"
                                      >
                                        <Typography
                                          fontSize="16px"
                                          fontWeight="500"
                                        >
                                          {row?.employee_name ?? "-"}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                    <Grid container>
                                      <Grid
                                        item
                                        display="flex"
                                        alignItems="center"
                                      >
                                        <Typography
                                          fontSize="14px"
                                          fontWeight="400"
                                        >
                                          {row?.employee_position ?? "-"}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Box>



                              {scoreColumnList.map((column, i) => {
                                let color_code = row[column.field]?.code ?? ''
                                let label = row[column.field] ? (row[column.field]?.label ?? row[column.field] ?? '-') : '-'
                                return (
                                  <Box key={i}
                                    sx={{ width: getScoreColumnWidth() }}
                                    pr="15px"
                                  >
                                    <Typography
                                      fontWeight="500"
                                      fontSize="14px"
                                      color={getClassificationListColor(color_code)}
                                    >
                                      {label}
                                    </Typography>
                                  </Box>
                                )
                              })}

                              {/*
                              <Box
                                sx={{ width: columnWidths.score[1] }}
                                pr="15px"
                              >
                                <Typography
                                  fontWeight="500"
                                  fontSize="14px"
                                  color={getClassificationListColor(
                                    row?.classification?.code
                                  )}
                                >
                                  {row?.classification?.label &&
                                    row?.classification?.label !== ""
                                    ? row.classification.label
                                    : "-"}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  width: columnWidths.score[1],
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  alignItems: "center",
                                }}
                                pr="15px"
                              >
                                <Typography fontWeight="400" fontSize="14px">
                                  {row?.performance?.label &&
                                    row?.performance?.label !== ""
                                    ? row?.performance?.label
                                    : "-"}
                                </Typography>
                              </Box>
                              <Box
                                sx={{ width: columnWidths.score[1] }}
                                pr="15px"
                              >
                                <Typography fontWeight="400" fontSize="14px">
                                  {row?.potential?.label &&
                                    row?.potential?.label !== ""
                                    ? row?.potential?.label
                                    : "-"}
                                </Typography>
                              </Box>
                              <Box
                                sx={{ width: columnWidths.score[1] }}
                                pr="15px"
                              >
                                <Typography
                                  fontWeight="400"
                                  fontSize={16}
                                  lineHeight="23.92px"
                                  sx={{
                                    width: "100%",
                                  }}
                                >
                                  {row?.overall_rating ?? "-"}
                                </Typography>
                              </Box>
                              <Box
                                sx={{ width: columnWidths.score[1] }}
                                pr="15px"
                              >
                                <Typography
                                  fontWeight="400"
                                  fontSize={16}
                                  lineHeight="23.92px"
                                  sx={{
                                    width: "100%",
                                  }}
                                >
                                  {row?.performance_rating ?? "-"}
                                </Typography>
                              </Box>
                              <Box
                                sx={{ width: columnWidths.score[1] }}
                                pr="15px"
                              >
                                <Typography
                                  fontWeight="400"
                                  fontSize={16}
                                  lineHeight="23.92px"
                                  sx={{
                                    width: "100%",
                                  }}
                                >
                                  {row?.potential_rating ?? "-"}
                                </Typography>
                              </Box>
*/}


                            </Box>
                          </Box>
                          <Divider width="100%" />
                        </Box>
                      );
                    }
                  }
                })
              ) : (
                <Box
                  sx={{
                    pt: "50px",
                    pb: "50px",
                    textAlign: "center",
                  }}
                >
                  <Nodata />
                  <Typography
                    color="#A0A0A0"
                    fontWeight="500"
                    fontSize={20}
                    lineHeight="30px"
                  >
                    {t("label.noDataFilter")}
                  </Typography>
                  <Typography
                    color="#A0A0A0"
                    fontWeight="400"
                    fontSize={16}
                    lineHeight="24px"
                  >
                    {t("label.tryChangingFilter")}
                  </Typography>
                </Box>
              )}
              <Grid container mt={1} spacing={2} px="20px" pb="24px">
                <Grid item xs sm md display="flex" alignItems="center">
                  <Box mr={1}>
                    <Typography variant="s14_fw400">
                      {t("label.showMaxItems")}
                    </Typography>
                  </Box>
                  <Select
                    id="pagination"
                    variant="outlined"
                    value={perPage}
                    onChange={handlePerPageChange}
                    displayEmpty
                    size="small"
                    className="white-bg"
                  >
                    <MenuItem value={50}>
                      <Typography style={{ fontWeight: "300" }}>50</Typography>
                    </MenuItem>
                    <MenuItem value={100}>
                      <Typography style={{ fontWeight: "300" }}>100</Typography>
                    </MenuItem>
                    <MenuItem value={200}>
                      <Typography style={{ fontWeight: "300" }}>200</Typography>
                    </MenuItem>
                  </Select>
                </Grid>
                <Grid item display="flex" alignItems="center" ml="auto">
                  <Typography variant="s14_fw400">
                    {t("label.page")} {currentPage} {t("label.from")}{" "}
                    {totalPage}
                  </Typography>
                </Grid>
                <Grid item ml="auto" display="flex" alignItems="center">
                  <CustomPagination
                    setCurrentPage={handleSetCurrentPage}
                    selected={currentPage}
                    count={totalPage}
                  ></CustomPagination>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Grid>
      </Grid>
      {Util.ScrollToTop()}
    </>
  );
};
export default EvaluationManagerDashboard;
